import { Modal } from '@fozito/common-ui';
import { Form } from 'antd';
import { debounce } from 'lodash';
import { PAYMENT_TYPE } from 'modules/POS/Cashier/cashier.constants';
import { IUICashierFormValues } from 'modules/POS/Cashier/cashier.interface';
import ModalActions from 'modules/POS/Cashier/components/ModalActions';
import { useSocketContext } from 'modules/POS/Cashier/hooks/useSocket';
import { ICustomerPaymentDetail, TypePaymentActors, TypePaymentPassData } from 'modules/POS/Cashier/socket.interface';
import cashierApis from 'modules/POS/services/apis';
import { IBillInfoResData } from 'modules/POS/services/types/bill';
import { ICashierCashPayment } from 'modules/POS/services/types/payment';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { parseDecimal } from 'utils/number';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
import DollarInputPayment from '../../../components/DollarAmount/DollarInputPayment';
import { useDollarAmountInputRef } from '../../../components/DollarAmount/Input';
import DollarAmountKeyboard from '../../../components/DollarAmount/Keyboard';
import { cashierCtrl } from 'modules/POS/Cashier/cashier.controller';
import useListenRequestSync from 'modules/POS/Cashier/hooks/useListenRequestSync';
const FormStyled = styled(Form)`
  .dollar-input-selector {
    justify-content: center;
    border: none !important;
    height: unset;
    padding: 8px 0;
  }
  .dollar-input-text {
    color: #1D2129;
    text-align: center;
    font-family: Poppins;
    font-size: 40px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;
export type ICashierCashPaymentMethod = (billId: string, payment: ICashierCashPayment) => Promise<boolean>;
type Props = {
  cashIncentive?: IUICashierFormValues['cashIncentive'];
  cashIncentiveAmount?: IUICashierFormValues['cashIncentiveAmount'],
  enable?: boolean;
  onPayment: ICashierCashPaymentMethod;
};
type Ref = {
  open: (billId: string) => Promise<void>;
};
export const useModalCashPaymentRef = () => useRef<Ref>(null);
const ModalCashPayment = forwardRef<Ref, Props>(({ enable, cashIncentiveAmount = 0, onPayment }, ref) => {
  const [form] = Form.useForm();
  const inputRefV2 = useDollarAmountInputRef();
  const socketContext = useSocketContext();
  const [billId, setOpen] = useState<string>('');
  const ticketNumber = Form.useWatch(['detail', 'ticketNumber'], form);
  const onClose = () => {
    socketContext.backToCheckIn();
    setOpen('');
  };

  const calcChance = (total: number, amount: number) => {
    const remaining = total - amount;
    const chance = remaining >= 0 ? 0 : remaining;
    return parseDecimal(chance);
  };
  const fetchDetail = async (_billId: string) => {
    try {
      const res: IResponseData<IBillInfoResData> = await cashierApis.getBillById(_billId);
      const detail = res.data.data;
      if (detail) {
        cashierCtrl.switchTicketWithDetailData(detail);
        // TODO: SOCKET
        const amountFinal = detail.total - cashIncentiveAmount;

        form.setFieldValue('detail', detail);
        form.setFieldValue('amount', amountFinal);

        socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
          billId,
          actor: TypePaymentActors.CASHIER,
          action: TypePaymentPassData.PASS_DATA,
          data: {
            cash: amountFinal,
            remaining: 0,
            chanceCash: 0,
            loyalty: 0,
            giftCard: 0,
            checkOthers: 0,
            card: 0,
            multiPay: 0,
            totalValue: amountFinal,
            subTotal: detail.subTotal || 0,
            cardFee: 0,
            saleTax: detail.saleTax || 0,
            usedTax: detail.useTax || 0,
            discount: (detail.discount || 0) + (detail.discountItem || 0) + (cashIncentiveAmount || 0),
          } as ICustomerPaymentDetail,
        });

        return detail;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  useListenRequestSync(async (_billId) => {
    if (!billId) return;
    await fetchDetail(_billId);
  });

  const onOpen: Ref['open'] = async (_billId) => {
    socketContext.switchCusPayment(_billId);
    try {
      const detail = await fetchDetail(_billId);
      if (detail)
        setOpen(_billId);
    } catch (error) {
    }
  };

  const handleFinish = async (values: any) => {
    const data = values as { amount: number, detail: IBillInfoResData };
    const payment: ICashierCashPayment = {
      amount: data.amount,
      paymentType: PAYMENT_TYPE.CASH,
      cashIncentiveDiscount: enable,
      chance: calcChance(data.detail.total, data.amount),
      discountIncentive: cashIncentiveAmount,
    };
    try {
      const isCompleted = await onPayment(data.detail.billId, payment);
      if (!isCompleted) return;
      setOpen('');
    } catch (error) { }
  };


  const onValuesChange = debounce(() => {
    const detail = form.getFieldValue('detail');
    const amount = form.getFieldValue('amount') || 0;

    const total = detail?.total || 0;
    const amountFinal = total - cashIncentiveAmount;

    const remaining = amountFinal - amount;
    const chance = calcChance(amountFinal, amount);

    socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
      billId,
      actor: TypePaymentActors.CASHIER,
      action: TypePaymentPassData.PASS_DATA,
      data: {
        cash: amount,
        remaining: remaining,
        chanceCash: chance,
        loyalty: 0,
        giftCard: 0,
        checkOthers: 0,
        card: 0,
        multiPay: 0,
        totalValue: amountFinal,
        subTotal: detail?.subTotal || 0,
        cardFee: 0,
        saleTax: detail?.saleTax || 0,
        usedTax: detail?.useTax || 0,
        discount: (detail?.discount || 0) + (detail?.discountItem || 0) + (cashIncentiveAmount || 0),
      } as ICustomerPaymentDetail,
    });
  }, 1000);

  useImperativeHandle(ref, () => ({
    open: onOpen
  }));

  if (!billId) return null;

  return (
    <Modal title={`CASH TICKET #${ticketNumber}`} open={!!billId} onClose={onClose} width={900}>
      <FormStyled form={form} onFinish={handleFinish} onValuesChange={onValuesChange}>
        <Form.Item name={'detail'} noStyle />
        <Form.Item name={'amount'} noStyle rules={[{
          validator(_, value) {
            const total = form.getFieldValue(['detail', 'total']);
            if (!value) return Promise.reject('Please enter amount');
            if (value < (total - cashIncentiveAmount)) return Promise.reject('Please enter amount');
            return Promise.resolve();
          },
        }]}>
          <DollarInputPayment ref={inputRefV2} inputMode='none' withForm />
        </Form.Item>
        <Form.Item shouldUpdate noStyle dependencies={['amount']}>
          {({ getFieldValue, getFieldError }) => {
            const total = getFieldValue('detail').total - cashIncentiveAmount;
            const amount = getFieldValue('amount') || 0;
            const remaining = total - amount;
            const chance = calcChance(total, amount);
            const errors = getFieldError('amount');
            console.log('errors', errors);

            return (
              <ContainerAmount>
                <div className="amount-item">
                  <div className="amount-label">Balance:</div>
                  <div className="amount-value">{formatCurrency(remaining < 0 ? 0 : remaining)}</div>
                </div>
                <div className="amount-item">
                  <div className="amount-label">Change:</div>
                  <div className="amount-value">{formatCurrency(chance)}</div>
                </div>
              </ContainerAmount>
            );
          }}
        </Form.Item>
        <Form.Item noStyle name={'amount'}>
          <DollarAmountKeyboard
            controlRef={inputRefV2}
          />
        </Form.Item>
        <ModalActions onClose={onClose} />
      </FormStyled>
    </Modal>
  );
});
ModalCashPayment.displayName = 'ModalCashPayment';
export default ModalCashPayment;

const ContainerAmount = styled.div`
display: flex;
height: 49px;
justify-content: center;
align-items: center;
gap: 32px;
align-self: stretch;
background: #F5F5F5;
margin: 0 32px;
.amount-item {
  display: flex;
  align-items: center;
  gap: 6px;
  .amount-label {
    color: #1D2129;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
  .amount-value {
    color: #1D2129;
    font-family: Poppins;
    font-size: 26px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
  }
}
`;